import { useState } from "react";
import SectionHeading from "./SectionHeading";

const SUBMIT_ADDR = "https://readdy.ai/api/form/daftsnbep053ij1ubvag";

const CATEGORIES = ["想聊聊意见", "给热门话题投稿", "希望合作", "其他"];
const CONTACTS = [
  { icon: "ri-mail-line", label: "反馈邮箱", value: "feedback@wuai-chigua.example" },
  { icon: "ri-time-line", label: "响应时间", value: "工作日 24 小时内回复" },
  { icon: "ri-megaphone-line", label: "投稿建议", value: "欢迎推荐你眼中的好瓜" },
];

type Status = "idle" | "loading" | "success" | "error";

export default function Contact() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");

  const resetToIdle = () => {
    setStatus("idle");
    setFormError("");
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);

    // honeypot guard: if a bot filled it, fake a success without sending anything
    const honeypot = String(data.get("website_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      setFormError("");
      form.reset();
      window.setTimeout(resetToIdle, 4000);
      return;
    }

    setStatus("loading");
    setFormError("");

    const payload: Record<string, string> = {};
    data.forEach((value, key) => {
      const v = String(value).trim();
      if (key !== "website_alt" && v) payload[key] = v;
    });

    try {
      const res = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(payload).toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = responseText ? JSON.parse(responseText) : null;
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.meta?.detail || (parsed ? "" : responseText) || "";

      if (res.ok && parsed?.code === "OK") {
        setStatus("success");
        setFormError("");
        form.reset();
        window.setTimeout(resetToIdle, 5000);
        return;
      }
      setStatus("error");
      setFormError(serverMsg || "提交失败，请稍后再试。");
    } catch {
      setStatus("error");
      setFormError("网络似乎开小差了，请稍后再试。");
    }
  };

  return (
    <section id="contact" className="bg-background-50 py-16 md:py-20">
      <div className="mx-auto w-full max-w-6xl px-4 md:px-6">
        <SectionHeading
          eyebrow="意见反馈 · 投稿合作"
          title="有好瓜、好建议？"
          accent="欢迎找我们聊"
          desc="无论是想让某个话题上热榜，还是对社区有更好的想法，都欢迎告诉我们，我们一起把瓜田经营得更热闹。"
        />

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 items-stretch">
          <div className="lg:col-span-2 flex flex-col rounded-2xl bg-gradient-to-br from-primary-500 to-primary-600 p-6 md:p-7 text-background-50">
            <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-background-50/15 text-2xl">🙌</span>
            <h3 className="font-heading mt-5 text-2xl leading-snug">你的一句话，可能就是明天的热榜</h3>
            <p className="mt-3 text-sm leading-relaxed text-background-100">
              期待每一位瓜友的参与。你的每一条反馈，都会被认真对待；你的每一个好瓜，都值得被大家看见。
            </p>
            <div className="mt-6 flex flex-col gap-4 border-t border-background-50/20 pt-6">
              {CONTACTS.map((c) => (
                <div key={c.label} className="flex items-center gap-3">
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-background-50/15">
                    <i className={c.icon}></i>
                  </span>
                  <div>
                    <p className="text-xs text-background-200">{c.label}</p>
                    <p className="text-sm text-background-50">{c.value}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-3 rounded-2xl bg-background-100/80 p-6 md:p-8 ring-1 ring-background-200/70">
            <form
              onSubmit={handleSubmit}
              data-readdy-form="吾爱吃瓜 意见反馈与投稿表单"
              className="flex flex-col gap-4"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex flex-col gap-1.5">
                  <label htmlFor="cf-name" className="text-sm text-foreground-700">
                    你的称呼 <span className="text-primary-600">*</span>
                  </label>
                  <input
                    id="cf-name"
                    name="name"
                    type="text"
                    required
                    placeholder="怎么称呼你"
                    className="rounded-lg border-0 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 ring-1 ring-background-200 placeholder:text-foreground-400 focus:ring-2 focus:ring-primary-300 outline-none"
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label htmlFor="cf-email" className="text-sm text-foreground-700">
                    联系邮箱 <span className="text-primary-600">*</span>
                  </label>
                  <input
                    id="cf-email"
                    name="email"
                    type="email"
                    required
                    placeholder="方便我们回复你"
                    className="rounded-lg border-0 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 ring-1 ring-background-200 placeholder:text-foreground-400 focus:ring-2 focus:ring-primary-300 outline-none"
                  />
                </div>
              </div>

              <div className="flex flex-col gap-1.5">
                <label htmlFor="cf-type" className="text-sm text-foreground-700">
                  反馈类型
                </label>
                <select
                  id="cf-type"
                  name="type"
                  defaultValue={CATEGORIES[0]}
                  className="rounded-lg border-0 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 ring-1 ring-background-200 focus:ring-2 focus:ring-primary-300 outline-none"
                >
                  {CATEGORIES.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex flex-col gap-1.5">
                <div className="flex items-center justify-between">
                  <label htmlFor="cf-message" className="text-sm text-foreground-700">
                    具体内容 <span className="text-primary-600">*</span>
                  </label>
                  <span className="text-xs text-foreground-400">500 字以内</span>
                </div>
                <textarea
                  id="cf-message"
                  name="message"
                  required
                  maxLength={500}
                  rows={5}
                  placeholder="说说你的想法、想推荐的话题，或对社区的建议…"
                  className="resize-none rounded-lg border-0 bg-background-50 px-4 py-3 text-sm text-foreground-900 ring-1 ring-background-200 placeholder:text-foreground-400 focus:ring-2 focus:ring-primary-300 outline-none"
                ></textarea>
              </div>

              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="anti-spam-field"
                placeholder="website_alt"
              />

              {status === "success" && (
                <p className="rounded-lg bg-accent-100 px-4 py-3 text-sm text-accent-800">
                  <i className="ri-checkbox-circle-fill mr-1.5"></i>
                  收到啦！感谢你的反馈，我们会尽快跟进处理。
                </p>
              )}
              {status === "error" && formError && (
                <p className="rounded-lg bg-primary-100 px-4 py-3 text-sm text-primary-800">
                  <i className="ri-error-warning-fill mr-1.5"></i>
                  {formError}
                </p>
              )}

              <button
                type="submit"
                disabled={status === "loading"}
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm text-background-50 hover:bg-primary-600 disabled:opacity-60 transition-colors"
              >
                {status === "loading" ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i>
                    提交中…
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-fill"></i>
                    提交反馈
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}